import React from "react";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { useSubscriptionContext } from "lib/contexts/SubscriptionProvider";
import useSendAnalyticsEvent from "lib/hooks/useAnalytics";
import { useLocale } from "lib/hooks/useLocale";
import { PurchaseMode } from "lib/types/subscriptions";
import { analyticsEvents } from "lib/utils/constants";
import { classNames } from "lib/utils/helpers";
import { nudgeTextGradient } from "lib/utils/subscriptions/planFormatters";

const BRAND_PRIMARY = "var(--flo-primary-dark-color)";
const TAB_TEXT_COLOR = "var(--Core-zinc-700, #3F3F46)";

const PurchaseModeTabs: React.FC = () => {
  const { t } = useLocale();
  const {
    state: { isEligible, mode, isSwitching },
    actions: { setMode },
  } = useSubscriptionContext();
  const {
    state: { checkoutMetadata },
  } = useMerchantContext();
  const { sendAnalyticsEvent } = useSendAnalyticsEvent();

  const config = checkoutMetadata?.uiAttributes?.subscriptionPlanConfig;
  const savingsText = config?.tabNudge || null;

  const savingsGradient = nudgeTextGradient(config?.subscriptionCard?.highlightBackgroundColor);

  if (!isEligible) return null;

  const handleSelect = (nextMode: PurchaseMode) => {
    if (nextMode === mode || isSwitching) return;

    sendAnalyticsEvent({
      eventName: analyticsEvents.FLO_SUBSCRIPTION_MODE_SELECTED,
      eventType: "click",
      metaData: { purchaseMode: nextMode },
    });
    setMode(nextMode);
  };

  const renderTab = (tabMode: PurchaseMode, label: string, subLabel?: string | null) => {
    const isSelected = mode === tabMode;

    return (
      <button
        id={`purchase-mode-${tabMode.toLowerCase()}`}
        role="tab"
        aria-selected={isSelected}
        disabled={isSwitching}
        onClick={() => handleSelect(tabMode)}
        className={classNames(
          "relative flex h-14 flex-1 flex-col items-center justify-center self-stretch px-6 transition-colors",
          isSelected ? "bg-white" : "bg-transparent",
          isSelected ? "" : "[@media(hover:hover)]:hover:bg-black/[0.04]",
          isSwitching ? "cursor-wait opacity-60" : "cursor-pointer",
        )}>
        <span
          className="h-4 flex text-text-sm-semibold items-center"
          style={{ color: TAB_TEXT_COLOR, opacity: isSelected ? 1 : 0.7 }}>
          {label}
        </span>
        {Boolean(subLabel) && (
          <span
            className="animate-gradient-sweep bg-clip-text text-text-xs-semibold text-transparent opacity-80"
            style={{ backgroundImage: savingsGradient, backgroundSize: "200% 100%" }}>
            {subLabel}
          </span>
        )}
        <span
          className="absolute inset-x-0 bottom-0 h-[2.5px]"
          style={{ backgroundColor: BRAND_PRIMARY, opacity: isSelected ? 0.8 : 0.1 }}
        />
      </button>
    );
  };

  return (
    <div
      id="purchase-mode-tabs"
      role="tablist"
      aria-label={t("subscription_mode_tabs_label")}
      className="flex w-full bg-gray-lightest shadow-[0px_1px_0px_0px_rgba(0,0,0,0.10)]">
      {renderTab("PAY_ONCE", t("subscription_pay_once"))}
      {renderTab("SUBSCRIBE", t("subscription_subscribe"), savingsText)}
    </div>
  );
};

export default PurchaseModeTabs;
